<template>
  <div class="auth-callback">
    <div class="auth-content">
      <!-- Spinner -->
      <div class="spinner-container">
        <svg class="spinner" viewBox="0 0 50 50">
          <circle class="spinner-path" cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>
        </svg>
      </div>
      
      <!-- Message -->
      <h2 class="auth-title">{{ $t(message) }}</h2>
      <p class="auth-subtitle">{{ $t(subtitle) }}</p>
      
      <!-- Redirect Button (shows only on error or after success) -->
      <v-btn
        v-if="showRedirectButton"
        @click="handleRedirect"
        color="primary"
        size="large"
        rounded
        elevation="0"
        class="redirect-btn"
      >
        {{ $t(redirectButtonText) }}
        <v-icon end>mdi-arrow-right</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useOAuthService } from '@/services/OAuthService'
import { getStoredItem, setStoredItem } from '@/services/utils'
import { useRouter } from 'vue-router'
import { getCustomer } from '@/assets/Branding/branding.js'

export default {
  name: 'AuthCallback',
  setup() {
    const router = useRouter()
    const { setToken, isLoggedIn } = useOAuthService()
    
    const message = ref('auth.authenticating')
    const subtitle = ref('auth.please-wait')
    const showRedirectButton = ref(false)
    const redirectButtonText = ref('auth.continue')
    const redirectTo = ref('/')

    const handleRedirect = () => {
      router.push(redirectTo.value)
    }

    const authenticateUser = async () => {
      try {
        console.log('[AuthCallback] - Starting authentication process.')
        const url = window.location.href
        const hash = url.includes('#') ? url.substring(url.indexOf('#') + 1) : ''
        const params = new URLSearchParams(hash)
        const accessToken = params.get('access_token')

        if (!accessToken) {
          throw new Error('auth.no-access-token')
        }

        console.log('[AuthCallback] - Access token found, setting token.')
        message.value = 'auth.verifying-credentials'
        await setToken(accessToken)

        // Handle building selection
        message.value = 'auth.setting-up-workspace'
        let storedBuilding = await getStoredItem('selectedBuilding')
        
        if (!storedBuilding || storedBuilding === ' Villa') {
          const { getBuildings } = await import('@/controllers/BaseController')
          const customer = await getCustomer()
          const apiBuildings = await getBuildings()

          if (customer === 'pia') {
            const building = apiBuildings.find(b => b.id === 'e1e1c326-f8e5-472b-9ea7-673c59550d46')
            if (building) storedBuilding = building.name
          }

          storedBuilding = storedBuilding || apiBuildings[0]?.name || null
          await setStoredItem('selectedBuilding', storedBuilding)
          console.log(`[AuthCallback] - Building set to: ${storedBuilding}`)
        }

        // Verify login
        if (await isLoggedIn()) {
          console.log('[AuthCallback] - Logged in successfully.')
          message.value = 'auth.login-successful'
          subtitle.value = 'auth.redirecting'
          
          // Small delay to show success message
          setTimeout(() => {
            router.push('/')
          }, 800)
        } else {
          throw new Error('auth.login-verification-failed')
        }
      } catch (error) {
        console.error('[AuthCallback] - Authentication error:', error)
        message.value = 'auth.authentication-failed'
        subtitle.value = error.message === 'auth.no-access-token' || error.message === 'auth.login-verification-failed' 
          ? error.message 
          : 'auth.something-went-wrong'
        showRedirectButton.value = true
        redirectButtonText.value = 'auth.back-to-login'
        redirectTo.value = '/login'
      }
    }

    onMounted(() => {
      authenticateUser()
    })

    return {
      message,
      subtitle,
      showRedirectButton,
      redirectButtonText,
      handleRedirect
    }
  }
}
</script>

<style scoped>
.auth-callback {
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  min-height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: fixed;
  top: 0;
  left: 0;
  overflow: hidden;
}

.auth-callback::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(66, 135, 245, 0.03) 0%, transparent 70%);
  animation: pulse 8s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.1); opacity: 0.8; }
}

.auth-content {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 400px;
  width: 100%;
}

.spinner-container {
  margin: 0 auto 32px;
  width: 80px;
  height: 80px;
}

.spinner {
  animation: rotate 2s linear infinite;
  width: 100%;
  height: 100%;
}

.spinner-path {
  stroke: #4287f5;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
  filter: drop-shadow(0 0 8px rgba(66, 135, 245, 0.4));
}

@keyframes rotate {
  100% { transform: rotate(360deg); }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

.auth-title {
  font-size: 28px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 12px 0;
  letter-spacing: -0.5px;
}

.auth-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 32px 0;
  line-height: 1.5;
}

.redirect-btn {
  margin-top: 16px;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.3px;
  padding: 12px 32px !important;
  font-size: 15px;
}

@media (max-width: 600px) {
  .auth-callback {
    padding: 16px;
  }

  .spinner-container {
    width: 64px;
    height: 64px;
    margin-bottom: 24px;
  }

  .auth-title {
    font-size: 24px;
  }

  .auth-subtitle {
    font-size: 14px;
  }

  .redirect-btn {
    width: 100%;
    max-width: 280px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .spinner,
  .spinner-path,
  .auth-callback::before {
    animation: none;
  }
  
  .spinner-path {
    stroke-dasharray: 90, 150;
  }
}
</style>